import { useState } from 'react'
import { DownloadSimple, Play } from '@phosphor-icons/react'
import { Input, useToast } from '@/ui'
import { errorMessage } from '../../api/core'
import { downloadRecall, useRecallExport, type RecallExport } from '../../api/pack'
import { ActionButton } from '../../components/Can'
import { BoardPage, Counter, Counters, Note, Pill } from '../../components/board'
import { useUrlState } from '../../lib/useUrlState'
import { boardDate, boardWhen } from '../../model/payoutBoard'
import { kg2, packLotState } from '../../model/packing'
import { balanceShares, seconds, stillAtHall } from '../../model/recall'
import { BoardCard } from '../packing/shared'

/**
 * Mock recall (alignment map V1-F7-09b MockRecall; F7.9): from any pack lot, batch, lot or sack-tag
 * code, every farmer and lot upstream, everything made from them, the customers reached and what
 * is still at the hall, with each farmer lot's balance. A read only: nothing is held. The export
 * (CSV or PDF) carries the same figures.
 */
export function MockRecallPage() {
  const [f, setF] = useUrlState({ code: '' })
  const [typed, setTyped] = useState(f.code)
  const q = useRecallExport(f.code || null)
  const toast = useToast()
  const [downloading, setDownloading] = useState(false)
  const download = async (format: 'csv' | 'pdf') => {
    if (!f.code) return
    setDownloading(true)
    try {
      await downloadRecall(f.code, format)
    } catch (e) {
      toast.error('Export not downloaded', errorMessage(e))
    } finally {
      setDownloading(false)
    }
  }
  return (
    <BoardPage
      title="Mock recall"
      actions={
        <>
          <ActionButton
            perm="recalls:read"
            variant="secondary"
            leadingIcon={<DownloadSimple />}
            disabled={!q.data}
            loading={downloading}
            onClick={() => void download('csv')}
          >
            Download export
          </ActionButton>
          <ActionButton
            perm="recalls:read"
            variant="secondary"
            disabled={!q.data}
            loading={downloading}
            onClick={() => void download('pdf')}
          >
            PDF
          </ActionButton>
        </>
      }
    >
      <form
        className="flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault()
          setF({ code: typed.trim() })
        }}
      >
        <label className="flex min-w-[260px] flex-1 flex-col gap-1 text-[15px]">
          <span className="font-bold">
            Pack lot, batch or lot code (from a pouch, a sack tag or typed)
          </span>
          <Input
            autoFocus
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder="PK-…"
          />
        </label>
        <ActionButton
          perm="recalls:read"
          type="submit"
          leadingIcon={<Play />}
          loading={q.isFetching}
          disabled={!typed.trim()}
        >
          Run
        </ActionButton>
      </form>
      {q.error && (
        <Pill tone="bad" wrap>
          {errorMessage(q.error)}
        </Pill>
      )}
      {q.data && <Result r={q.data} />}
      {!f.code && (
        <Note>
          Run it once on the first released pack lot: the only end-to-end proof that a pouch traces
          back to the farmer and forward to the customer.
        </Note>
      )}
    </BoardPage>
  )
}

function Result({ r }: { r: RecallExport }) {
  const farmerName = (id: string | null) => {
    const f = r.farmers.find((x) => x.farmerId === id)
    return f?.name ?? f?.farmerCode ?? 'Farmer'
  }
  const atHall = r.packLots.map((p) => ({ ...p, left: stillAtHall(p) })).filter((p) => p.left > 0)
  return (
    <>
      <Counters>
        <Counter label="Time to complete" value={seconds(r.elapsedMs)} />
        <Counter label="Farmer lots" value={r.lots.length} />
        <Counter label="Pack lots" value={r.packLots.length} />
        <Counter label="Customers" value={new Set(r.customers.map((c) => c.orgId)).size} />
      </Counters>
      <Note className="text-ink">
        Run {boardWhen(r.generatedAt)}
        {r.generatedBy ? ` by ${r.generatedBy.name || r.generatedBy.username}` : ''} ·{' '}
        {r.lots.length} farmer lot{r.lots.length === 1 ? '' : 's'} · {r.runs.length} run
        {r.runs.length === 1 ? '' : 's'} · {r.batches.length} batch
        {r.batches.length === 1 ? '' : 'es'} · {r.packLots.length} pack lot
        {r.packLots.length === 1 ? '' : 's'} · {new Set(r.customers.map((c) => c.orgId)).size}{' '}
        customers
      </Note>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <BoardCard title="Farmers and lots upstream">
          {r.lots.map((l) => (
            <div key={l.unitId} className="flex flex-col">
              <b>{farmerName(l.farmerId)}</b>
              <span className="text-[14px] text-muted">{l.seasonCode ?? ''}</span>
              <span>
                {l.code} · {kg2(l.netG)}
              </span>
            </div>
          ))}
          {r.runs
            .filter((x) => x.stepCode !== 'PACK')
            .map((x) => (
              <div key={x.id} className="flex flex-col border-t border-rule pt-1.5">
                <b>{x.code}</b>
                <span className="text-[14px] text-muted">
                  {x.stepCode.toLowerCase()} · {boardDate(x.startedAt)} ·{' '}
                  {x.notClosed ? 'not closed' : x.status.toLowerCase()}
                </span>
              </div>
            ))}
        </BoardCard>
        <BoardCard title="Everything made from them">
          {r.batches.map((b) => (
            <div key={b.unitId} className="flex flex-col">
              <b>{b.code}</b>
              <span className="text-[14px] text-muted">
                {kg2(b.netG)} {b.stream === 'MAIN' ? 'brand' : 'red tie'}
                {b.hold !== 'NONE' ? ` · ${b.hold.toLowerCase()}` : ''}
              </span>
            </div>
          ))}
          {r.packLots.map((p) => {
            const s = packLotState(p.status, p.hold)
            return (
              <div key={p.id} className="flex flex-col gap-0.5 border-t border-rule pt-1.5">
                <b>{p.code}</b>
                <Pill tone={s.tone}>{s.text}</Pill>
                <span className="text-[14px] text-muted">
                  {p.pouches ?? '—'} pouches · {p.pouchesDispatched} dispatched
                </span>
              </div>
            )
          })}
          {r.otherOutputs.length > 0 && (
            <div className="flex flex-col border-t border-rule pt-1.5">
              <b>Not brand (no claim)</b>
              {r.otherOutputs.map((o) => (
                <span key={o.unitId} className="text-[14px]">
                  {o.unitType === 'BYPRODUCT'
                    ? 'By-product'
                    : o.stream === 'CONV'
                      ? 'Red tie (ordinary)'
                      : o.unitType.toLowerCase()}{' '}
                  {o.code}: {kg2(o.netG)}
                </span>
              ))}
            </div>
          )}
        </BoardCard>
        <BoardCard title="Customers reached">
          {r.customers.length === 0 && <Note>No pouches dispatched yet.</Note>}
          {r.customers.map((c) => (
            <div key={`${c.shipmentCode}-${c.packLotCode}`} className="flex flex-col">
              <b>{c.org}</b>
              <span className="text-[14px] text-muted">
                {c.challanNo} · {boardDate(c.dispatchedAt)}
              </span>
              <span>
                {c.pouches} pouches of {c.packLotCode}
              </span>
            </div>
          ))}
          {atHall.map((p) => (
            <div key={p.id} className="flex flex-col border-t border-rule pt-1.5">
              <b>Still at the hall</b>
              <span>
                {p.left} pouches of {p.code} · {packLotState(p.status, p.hold).text.toLowerCase()},
                not yet sent
              </span>
            </div>
          ))}
        </BoardCard>
      </div>
      {r.balance.map((b) => (
        <BoardCard
          key={b.lotId}
          title={`Lot balance · ${b.lotCode}`}
          aside={
            <Pill
              tone={b.accountedPct !== null && b.accountedPct >= 98 && !b.overPacked ? 'ok' : 'bad'}
            >
              Accounted for {b.accountedPct ?? '—'}%
            </Pill>
          }
        >
          <div className="flex flex-col gap-1">
            {balanceShares(b).map((s) => (
              <div key={s.key} className="flex items-center gap-3 text-[15px]">
                <span className="w-64 shrink-0">{s.label}</span>
                <span className="w-24 text-right tabular">{kg2(s.g)}</span>
                <span className="w-16 text-right tabular text-muted">
                  {s.pct === null ? '—' : `${s.pct}%`}
                </span>
                <span className="hidden h-2 flex-1 rounded bg-paper-tint sm:block">
                  <span
                    className="block h-2 rounded bg-amber"
                    style={{ width: `${Math.max(0, Math.min(100, s.pct ?? 0))}%` }}
                  />
                </span>
              </div>
            ))}
          </div>
          {b.overPacked && (
            <Pill tone="bad">More brand kilos packed than the farmer delivered</Pill>
          )}
          <Note>
            Gate kilos {kg2(b.gateG)}. Packed kilos are pouches × pouch grams. These figures go into
            the export.
          </Note>
        </BoardCard>
      ))}
    </>
  )
}
